import { useState, useEffect, useCallback } from "react"
import { Container, Row, Col, Spinner, Card, Table, Badge } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import API from "../../common/API"
import DateRangeFilter from "../../components/indicators/DateRangeFilter"
import IndicatorCard from "../../components/indicators/IndicatorCard"
import moment from "moment"
import { DispatchDetailWarehouse, WarehouseCategory } from "../../types/Indicators.types"

interface Props {
  orgId: string
}

export default function WarehouseAnalysis({ orgId }: Props) {
  const { t } = useTranslation("global")

  const [isLoading, setIsLoading] = useState(false)
  const [warehouses, setWarehouses] = useState<DispatchDetailWarehouse[]>([])
  const [selectedWarehouse, setSelectedWarehouse] = useState("")
  const [categories, setCategories] = useState<WarehouseCategory[]>([])
  const [isLoadingCategories, setIsLoadingCategories] = useState(false)
  const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"))
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"))

  const bindData = useCallback(async (start: string, end: string) => {
    if (!orgId) return
    try {
      setIsLoading(true)
      setStartDate(start)
      setEndDate(end)
      setSelectedWarehouse("")
      setCategories([])
      const params = `organizationId=${orgId}&startDate=${start}&endDate=${end}`
      const res = await API.getAction("Indicators/DispatchDetailByWarehouse", params)
      if (res.data.status === "ok") setWarehouses(res.data.response ?? [])
    } catch (ex) { console.error(ex) }
    finally { setIsLoading(false) }
  }, [orgId])

  const loadCategories = async (warehouse: string) => {
    setSelectedWarehouse(warehouse)
    try {
      setIsLoadingCategories(true)
      const params = `organizationId=${orgId}&startDate=${startDate}&endDate=${endDate}&warehouse=${encodeURIComponent(warehouse)}`
      const res = await API.getAction("Indicators/DispatchWarehouseCategories", params)
      if (res.data.status === "ok") setCategories(res.data.response ?? [])
    } catch (ex) { console.error(ex) }
    finally { setIsLoadingCategories(false) }
  }

  useEffect(() => {
    setWarehouses([])
    setSelectedWarehouse("")
    setCategories([])
    if (orgId) bindData(startDate, endDate)
  }, [orgId])

  const totalQuantity = warehouses.reduce((acc, w) => acc + w.totalQuantity, 0)
  const totalDispatches = warehouses.reduce((acc, w) => acc + w.dispatchCount, 0)

  return (
    <Container fluid className="px-0 py-3">
      <h5 className="mb-3">
        <i className="fas fa-warehouse me-2 text-primary" />
        {t("By Warehouse")}
      </h5>

      <DateRangeFilter onFilter={(s, e) => bindData(s, e)} loading={isLoading} />

      {!orgId ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-4 text-center text-muted">
            {t("Select an organization to load dispatch indicators")}
          </Card.Body>
        </Card>
      ) : isLoading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col sm={6} lg={3}>
              <IndicatorCard title={t("Warehouses")} value={warehouses.length} icon="fa-warehouse" />
            </Col>
            <Col sm={6} lg={3}>
              <IndicatorCard title={t("Total Quantity Moved")} value={Math.round(totalQuantity)} icon="fa-boxes-stacked" />
            </Col>
            <Col sm={6} lg={3}>
              <IndicatorCard title={t("Dispatches")} value={totalDispatches} icon="fa-truck" />
            </Col>
          </Row>

          <Row className="g-3">
            <Col lg={selectedWarehouse ? 7 : 12}>
              <Card className="shadow-sm border-0">
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">{t("Warehouse")}</th>
                        <th className="text-end">{t("Total Qty")}</th>
                        <th className="text-end">{t("Dispatches")}</th>
                        <th className="text-end">{t("Products")}</th>
                        <th className="text-end pe-3">{t("Clients")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {warehouses.length === 0 && (
                        <tr><td colSpan={5} className="text-center text-muted py-4">{t("No data available")}</td></tr>
                      )}
                      {warehouses.map((w, i) => (
                        <tr
                          key={i}
                          role="button"
                          className={selectedWarehouse === w.warehouse ? "table-active" : ""}
                          onClick={() => loadCategories(w.warehouse)}
                        >
                          <td className="ps-3 fw-semibold">{w.warehouse}</td>
                          <td className="text-end">{w.totalQuantity.toLocaleString()}</td>
                          <td className="text-end"><Badge bg="secondary">{w.dispatchCount}</Badge></td>
                          <td className="text-end"><Badge bg="light" text="dark">{w.distinctProducts}</Badge></td>
                          <td className="text-end pe-3"><Badge bg="light" text="dark">{w.distinctClients}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
              {warehouses.length > 0 && !selectedWarehouse && (
                <div className="text-muted small mt-2">
                  <i className="fas fa-circle-info me-1" />
                  {t("Click a warehouse to see its categories")}
                </div>
              )}
            </Col>

            {selectedWarehouse && (
              <Col lg={5}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white fw-semibold">
                    <i className="fas fa-tags me-2 text-primary" />
                    {t("Categories in warehouse")}: {selectedWarehouse}
                  </Card.Header>
                  <Card.Body className="p-0">
                    {isLoadingCategories ? (
                      <div className="text-center py-4"><Spinner animation="border" size="sm" variant="primary" /></div>
                    ) : (
                      <Table hover responsive className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="ps-3">{t("Category")}</th>
                            <th className="text-end">{t("Total Qty")}</th>
                            <th className="text-end pe-3">{t("Dispatches")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {categories.length === 0 && (
                            <tr><td colSpan={3} className="text-center text-muted py-4">{t("No data available")}</td></tr>
                          )}
                          {categories.map((c, i) => (
                            <tr key={i}>
                              <td className="ps-3">{c.category}</td>
                              <td className="text-end">{c.totalQuantity.toLocaleString()}</td>
                              <td className="text-end pe-3"><Badge bg="secondary">{c.dispatchCount}</Badge></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        </>
      )}
    </Container>
  )
}
